import React, { useState } from 'react';

import ContentfulImage from '../../Common/ContentfulImage';
import SmartLink from '../../Common/SmartLink';
import OptionalCard from './OptionalCard';
import * as styles from './SubMenu.module.scss';

// Individual link in a menu
const Link = ({ link, setHoveredLinkIndex, hoveredLinkIndex, index }) => {
  const url = link.url || link.titleUrl;
  const description = link.descriptionText || link.description;

  const setIndex = () => (setHoveredLinkIndex && index !== undefined ? setHoveredLinkIndex(index) : null);

  const LinkGroup = () => (
    <>
      {link.icon && <ContentfulImage className={styles.icon} imageFragment={link.icon} />}
      {link.title}
      {description && <span className={styles.sectionDescription}>{description}</span>}
      {setHoveredLinkIndex && hoveredLinkIndex === index && (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 40">
          <path d="M0 20 13.5.081V39.92z"></path>
        </svg>
      )}
    </>
  );

  return url ? (
    <SmartLink className={[styles.link, link.icon ? styles.hasIcon : ''].join(' ')} to={url} onMouseEnter={setIndex}>
      <LinkGroup />
    </SmartLink>
  ) : (
    <div className={styles.link} onMouseEnter={setIndex}>
      <LinkGroup />
    </div>
  );
};

// Title and optional description for a menu
const Title = ({ menu }) => {
  const TitleGroup = () => (
    <div className={styles.titleGroup}>
      <div className={styles.sectionTitle}>{menu.sectionTitle}</div>
      {menu.description?.description && (
        <div className={styles.sectionDescription}>{menu.description?.description}</div>
      )}
    </div>
  );

  return menu.titleUrl ? (
    <SmartLink to={menu.titleUrl}>
      <TitleGroup />
    </SmartLink>
  ) : (
    <TitleGroup />
  );
};

// Splits a long menu into two columns
const SplitMenu = ({ menu }) => {
  const middleIndex = menu.menuItems.length / 2 + 1;
  const firstColumn = menu.menuItems.slice(0, middleIndex);
  const secondColumn = menu.menuItems.slice(middleIndex, menu.menuItems.length);

  return (
    <div className={styles.splitMenu}>
      <div>
        {firstColumn.map((link, i) => (
          <Link link={link} key={`${link.title}-${i}`} />
        ))}
      </div>
      <div>
        {secondColumn.map((link, i) => (
          <Link link={link} key={`${link.title}-${i}`} />
        ))}
      </div>
    </div>
  );
};

// List of links in a menu
const LinksList = ({ menu, hoveredLinkIndex, setHoveredLinkIndex }) => (
  <div className={styles.column}>
    <Title menu={menu} />
    {menu.menuItems.length > 6 ? (
      <SplitMenu menu={menu} />
    ) : (
      menu.menuItems.map((link, i) =>
        link.subMenuItems ? (
          <Link
            link={link}
            key={`${link.title}-${i}`}
            setHoveredLinkIndex={setHoveredLinkIndex}
            hoveredLinkIndex={hoveredLinkIndex}
            index={i}
          />
        ) : (
          <Link link={link} key={`${link.title}-${i}`} />
        )
      )
    )}
  </div>
);

// Tertiary menu to show upon hovering over a menu item
const TertiaryMenu = ({ menu, visible }) => (
  <div className={styles.flexWrapper} style={{ visibility: visible ? 'visible' : 'hidden' }}>
    <div className={[styles.column, styles.tertiaryMenu].join(' ')} key={menu.title}>
      {menu.subMenuItems.map((link, i) => (
        <Link link={link} key={`${link.title}-${i}`} />
      ))}
    </div>
  </div>
);

// Main component for a sub menu
const SubMenu = ({ menu }) => {
  const [hoveredLinkIndex, setHoveredLinkIndex] = useState(0);
  const menuItems = menu.menuItems;

  // Check if the menu is comprised of secondary menus rather than individual menu items (links)
  const isMultiMenu = menuItems.some((s) => s.__typename === 'ContentfulComponentMenu');

  // Reset the hovered link index after a delay (matching the nav CSS transition duration)
  const resetHoveredLinkIndex = () => (hoveredLinkIndex !== 0 ? setTimeout(() => setHoveredLinkIndex(0), 300) : null);

  return (
    <div className={styles.subNav} onMouseLeave={resetHoveredLinkIndex}>
      {isMultiMenu ? (
        menuItems.map((menu, i) => <LinksList menu={menu} key={i} />)
      ) : (
        <LinksList menu={menu} hoveredLinkIndex={hoveredLinkIndex} setHoveredLinkIndex={setHoveredLinkIndex} />
      )}
      {menuItems.some((link) => link.subMenuItems) && (
        <div className={styles.menuInception}>
          {menuItems.map(
            (link, i) =>
              link.subMenuItems && (
                <TertiaryMenu menu={link} key={`${link.title}-${i}`} visible={hoveredLinkIndex === i} />
              )
          )}
        </div>
      )}
      {menu.optionalCard && <OptionalCard optionalCard={menu.optionalCard} />}
    </div>
  );
};

export default SubMenu;
